import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  Input,
  message,
} from 'antd';
import PropTypes from 'prop-types';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import NumericInput from 'components/NumericInput';
import currencyFormater from 'utils/toCurrency';
import { discartNullValues } from 'utils/formatValues';
import Select from 'components/Select';
import { createDecimalRegExp } from 'utils/patterns';
import { onError } from 'utils/handlers';
import {
  baseURI,
  deleteTarifa,
  getTarifas,
  patchTarifa,
  postTarifa,
} from 'api/configuracion/tarifas';
import { getModelPermission } from 'utils/permission/index';

export const model = 'tarifa';

const Tarifas = ({
  tipoDeTarifa,
  tarifa,
}) => {
  const permission = useMemo(() => getModelPermission(model), []);
  const [decimal, maxLength] = createDecimalRegExp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [required, setRequired] = useState(false);
  const [disabledSuperior, setDisabledSuperior] = useState(false);
  const [disabledInferior, setDisabledInferior] = useState(false);
  const [limiteInferior, setLimiteInferior] = useState(0);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState({ results: [] });
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [currentPage, setCurrentPage] = useState(1);

  const fetchData = async (page = 1, q = null, params = null) => {
    setLoading(true);
    setCurrentPage(page);
    const _data = await getTarifas({
      catalogo_de_tarifa: tarifa,
      page,
      ...q,
      ...params,
    });
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      fetchData();
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setRequired(false);
    setDisabledSuperior(false);
    setDisabledInferior(false);
    setLimiteInferior(0);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const [key] = selectedRowKeys;
      const formValues = form.getFieldsValue();
      const isConcept = [4, 5].includes(tipoDeTarifa);
      const values = isConcept ? formValues
        : discartNullValues(formValues, { numbers: true, regExp: decimal });
      values.concepto = formValues?.concepto;
      // if (isConcept) values.monto_minimo = 0;
      const saved = await (key ? patchTarifa(key, values) : postTarifa(values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    if (selectedRowKeys.length) {
      const [key] = selectedRowKeys;
      const deleted = await deleteTarifa(key);
      if (deleted) {
        setVisibleAlert(false);
        onCancel();
        await fetchData();
      }
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    type: 'radio',
    selectedRowKeys,
  };

  const onClickAdd = () => {
    setSelectedRowKeys([]);
    form.resetFields();
    setRequired(false);
    const limite_superior_anterior = [...data.results].pop()?.limite_superior || 0;
    setLimiteInferior(limite_superior_anterior);
    if (data.results.length
      && !limite_superior_anterior
      && tipoDeTarifa !== 4
      && tipoDeTarifa !== 5) {
      message.warn('El último registro no cuenta con Límite Superior');
    } else {
      form.setFieldsValue({
        catalogo_de_tarifa: tarifa,
        limite_inferior: limite_superior_anterior + 1,
      });
      setVisible(true);
    }
  };

  const onClickEdit = () => {
    setVisible(true);
    const [key] = selectedRowKeys;
    const itemIndex = data.results.findIndex((e) => e.id === key);
    setDisabledInferior(itemIndex !== 0);
    setDisabledSuperior(itemIndex !== data.results.length - 1);
    const last = [...data.results].pop();
    setLimiteInferior(disabledSuperior ? last?.limite_superior : 0);
    form.setFieldsValue({ catalogo_de_tarifa: tarifa });
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  // const decimalValidator = {
  //   validator: async (rule, val) => {
  //     if (val && !decimal.test(val)) {
  //       throw new Error('Ingrese un valor decimal válido');
  //     }
  //   },
  // };

  const requiredRule = { required: true };

  const conditionalRequired = (condition = false) => ({
    required: condition,
  });

  const rules = {
    required: [requiredRule],
    limite_inferior: [
      {
        validator: async (rule, val) => {
          if (!val && val !== 0) {
            throw new Error('El campo es requerido');
          } else if (!decimal.test(val)) {
            throw new Error('Ingrese un valor decimal válido');
          } else if (limiteInferior !== 0 && parseFloat(val) <= limiteInferior) {
            throw new Error(`El valor debe ser mayor a ${limiteInferior}`);
          }
        },
      },
    ],
    limite_superior: [
      conditionalRequired(selectedRowKeys[0] && data.results
        .findIndex((e) => e.id === selectedRowKeys[0]) !== data.results.length - 1),
      {
        validator: async (rule, val) => {
          const limite_inferior = form.getFieldValue('limite_inferior');
          if (val) {
            if (!decimal.test(val)) {
              throw new Error('Ingrese un valor decimal válido');
            } else if (parseFloat(val) <= parseFloat(limite_inferior)) {
              throw new Error('El valor debe ser mayor al Limite Inferior');
            }
          }
        },
      },
    ],
    required_conditionally: [
      conditionalRequired(required),
    ],
    decimalTarifaPorcentaje: [
      conditionalRequired(tipoDeTarifa === 1),
      // decimalValidator,
    ],
    decimalTarifaRango: [
      conditionalRequired(tipoDeTarifa === 2),
      // decimalValidator,
    ],
    decimalTarifaSimple: [
      conditionalRequired(tipoDeTarifa === 3),
      // decimalValidator,
    ],
    tarifaPorConcepto: [
      conditionalRequired(tipoDeTarifa === 4 || tipoDeTarifa === 3),
      // decimalValidator,
    ],
    concepto: [
      conditionalRequired(tipoDeTarifa === 4),
    ],
    monto_minimo: [
      conditionalRequired(tipoDeTarifa !== 4),
    ],
  };

  const columns1 = [
    {
      titleText: 'Límite Inferior',
      dataIndex: 'limite_inferior',
      key: 'limite_inferior',
      width: 200,
      align: 'right',
      render: (val) => currencyFormater(val || 0, true),
    },
    {
      titleText: 'Límite Superior',
      dataIndex: 'limite_superior',
      key: 'limite_superior',
      width: 200,
      align: 'right',
      render: (val) => currencyFormater(val || 0, true),
    },
    {
      titleText: 'Factor',
      dataIndex: 'factor',
      key: 'factor',
      width: 100,
      align: 'right',
      render: (val) => currencyFormater(val || 0, true),
    },
    {
      titleText: 'Cuota Fija',
      dataIndex: 'cuota_fija',
      key: 'cuota_fija',
      width: 150,
      align: 'right',
      render: (val) => currencyFormater(val || 0, true),
    },
    {
      titleText: 'Monto Mínimo',
      dataIndex: 'monto_minimo',
      key: 'monto_minimo',
      width: 200,
      render: (val) => currencyFormater(val || 0),
      align: 'right',
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (val) => (estadosGlobales.find((e) => e.id === val)?.descripcion),
    },
  ];

  const columns2 = [
    {
      titleText: 'Límite Inferior',
      dataIndex: 'limite_inferior',
      key: 'limite_inferior',
      width: 200,
      align: 'right',
      render: (val) => currencyFormater(val || 0, true),
    },
    {
      titleText: 'Límite Superior',
      dataIndex: 'limite_superior',
      key: 'limite_superior',
      width: 200,
      align: 'right',
      render: (val) => currencyFormater(val || 0, true),
    },
    {
      titleText: 'Rango',
      dataIndex: 'rango',
      key: 'rango',
      width: 150,
      align: 'right',
      render: (val) => currencyFormater(val || 0, true),
    },
    {
      titleText: 'Impuesto por Millar',
      dataIndex: 'impuesto_por_millar',
      key: 'impuesto_por_millar',
      width: 200,
      align: 'right',
      render: (val) => currencyFormater(val || 0, true),
    },
    {
      titleText: 'Impuesto por Rango',
      dataIndex: 'impuesto_por_rango',
      key: 'impuesto_por_rango',
      width: 200,
      align: 'right',
      render: (val) => currencyFormater(val || 0, true),
    },
    {
      titleText: 'Impuesto por Pagar',
      dataIndex: 'impuesto_por_pagar',
      key: 'impuesto_por_pagar',
      width: 150,
      align: 'right',
      render: (val) => currencyFormater(val || 0),
    },
    {
      titleText: 'Monto Mínimo',
      dataIndex: 'monto_minimo',
      key: 'monto_minimo',
      width: 200,
      render: (val) => currencyFormater(val || 0),
      align: 'right',
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (val) => (estadosGlobales.find((e) => e.id === val)?.descripcion),
    },
  ];

  const columns3 = [
    {
      titleText: 'Límite Inferior',
      dataIndex: 'limite_inferior',
      key: 'limite_inferior',
      width: 200,
      render: (val) => currencyFormater(val || 0, true),
      align: 'right',
    },
    {
      titleText: 'Límite Superior',
      dataIndex: 'limite_superior',
      key: 'limite_superior',
      width: 200,
      render: (val) => currencyFormater(val || 0, true),
      align: 'right',
    },
    {
      titleText: 'Impuesto por Millar',
      dataIndex: 'impuesto_por_millar',
      key: 'impuesto_por_millar',
      width: 200,
      render: (val) => currencyFormater(val || 0, true),
      align: 'right',
    },
    {
      titleText: 'Monto Mínimo',
      dataIndex: 'monto_minimo',
      key: 'monto_minimo',
      width: 200,
      render: (val) => currencyFormater(val || 0),
      align: 'right',
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (val) => (estadosGlobales.find((e) => e.id === val)?.descripcion),
    },
  ];

  const columns4 = [
    {
      titleText: 'Concepto',
      dataIndex: 'concepto',
      key: 'concepto',
      width: 200,
    },
    {
      titleText: 'Impuesto por Pagar',
      dataIndex: 'impuesto_por_pagar',
      key: 'impuesto_por_pagar',
      width: 200,
      render: (e) => currencyFormater(e || 0),
      align: 'right',
    },
    {
      titleText: 'Estado',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (val) => (estadosGlobales.find((e) => e.id === val)?.descripcion),
    },
  ];

  const columns = [
    columns1,
    columns2,
    columns3,
    columns4,
    columns4,
  ];

  const dinamicCols = () => {
    if (tipoDeTarifa === 2) {
      return 8;
    }
    return 24;
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          allowSort={false}
          cols={columns[tipoDeTarifa - 1]}
          data={data.results}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          RTLHead
          rowKey="id"
          allowImport
          baseURI={baseURI}
          permission={{ ...permission, import: true }}
          hasAllViewPermission
          pagination={false}
          paginationCallback={fetchData}
          currentPage={currentPage}
          count={data.count}
        />
      </Spin>
      <Modal
        visible={visible}
        title={(
          <FormSubmitControls
            label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Tarifa`}
            onFinish={() => onFinish()}
            onCancel={onCancel}
            loading={loading}
            permission={permission}
          />
        )}
        width={tipoDeTarifa === 2 ? '90%' : '60%'}
        keyboard={!loading}
        maskClosable={!loading}
        closable={false}
        onCancel={onCancel}
        footer={null}
      >
        <Spin tip="Cargando..." spinning={loading}>
          <Form
            layout="vertical"
            form={form}
            onFinish={() => onFinish()}
            scrollToFirstError
            initialValues={{ estados_globales: 1 }}
            onValuesChange={(changedValues) => {
              const prop = Object.keys(changedValues).shift();
              if (prop === 'limite_superior') {
                setRequired(!!changedValues.limite_superior);
              }
            }}
          >
            <Row gutter={10}>
              <Form.Item name="catalogo_de_tarifa" hidden>
                <Input allowClear />
              </Form.Item>
              {tipoDeTarifa === 2 && (
                <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                  <Form.Item
                    name="concepto"
                    rules={rules.required}
                    label="Concepto"
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
              )}
              {([1, 2, 3].includes(tipoDeTarifa)) && (
                <>
                  <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                    <NumericInput
                      name="limite_inferior"
                      rules={rules.limite_inferior}
                      label="Límite inferior"
                      maxLength={maxLength}
                      disabled={disabledInferior}
                    />
                  </Col>
                  <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                    <NumericInput
                      name="limite_superior"
                      rules={rules.limite_superior}
                      label="Límite superior"
                      disabled={disabledSuperior}
                      maxLength={maxLength}
                    />
                  </Col>
                </>
              )}
              {tipoDeTarifa === 1 && (
                <>
                  <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                    <NumericInput
                      name="factor"
                      rules={rules.decimalTarifaPorcentaje}
                      label="Factor"
                      decimal
                    />
                  </Col>

                  <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                    <NumericInput
                      name="cuota_fija"
                      rules={rules.decimalTarifaPorcentaje}
                      label="Cuota fija"
                      decimal
                    />
                  </Col>
                </>
              )}
              {tipoDeTarifa === 2 && (
                <>
                  <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                    <NumericInput
                      name="rango"
                      rules={rules.required_conditionally}
                      label="Rango"
                    />
                  </Col>
                  <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                    <NumericInput
                      name="impuesto_por_millar"
                      rules={rules.decimalTarifaRango}
                      label="Impuesto por millar"
                    />
                  </Col>
                  <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                    <NumericInput
                      label="Impuesto por rango"
                      name="impuesto_por_rango"
                      rules={rules.required_conditionally}
                    />
                  </Col>

                </>
              )}
              {tipoDeTarifa === 3 && (
                <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                  <NumericInput
                    name="impuesto_por_millar"
                    rules={rules.decimalTarifaSimple}
                    label="Impuesto por millar"
                  />
                </Col>
              )}
              {([4, 5].includes(tipoDeTarifa)) && (
                <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                  <Form.Item
                    name="concepto"
                    rules={rules.concepto}
                    label="Concepto"
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
              )}
              {([2, 4, 5].includes(tipoDeTarifa)) && (
                <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                  <NumericInput
                    name="impuesto_por_pagar"
                    required
                    label="Impuesto por pagar"
                    decimal
                  />
                </Col>
              )}
              {[4, 5].includes(tipoDeTarifa) && (
                <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                  <NumericInput
                    name="monto_minimo"
                    rules={rules.monto_minimo}
                    label="Valor Mínimo"
                    maxLength={maxLength}
                    decimal
                    // disabled={disabledInferior}
                  />
                </Col>
              )}
              <Col xs={24} sm={dinamicCols()} md={dinamicCols()}>
                <Form.Item
                  name="estados_globales"
                  label="Estado"
                  rules={rules.required}
                  hasFeedback
                >
                  <Select dataSource={estadosGlobales} disabled={!selectedRowKeys.length} />
                </Form.Item>
              </Col>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Row>
          </Form>
        </Spin>
      </Modal>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => setVisibleAlert(false)}
        visible={visibleAlert}
        content="Tarifa seleccionada"
        loading={loading}
      />
    </Row>
  );
};

Tarifas.propTypes = {
  tipoDeTarifa: PropTypes.number,
  tarifa: PropTypes.number,
  // permission: PropTypes.objectOf({
  //   view: PropTypes.bool,
  //   add: PropTypes.bool,
  //   change: PropTypes.bool,
  //   delete: PropTypes.bool,
  //   authorize: PropTypes.bool,
  //   cancel: PropTypes.bool,
  //   export: PropTypes.bool,
  //   import: PropTypes.bool,
  //   sync: PropTypes.bool,
  // }).isRequired,
};

Tarifas.defaultProps = {
  tipoDeTarifa: null,
  tarifa: null,
};

export default Tarifas;
